import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The state of the current Stripe payment process within a StripePaymentContext
 */
export type StripePaymentContextState =
  | "idle"
  /** Waiting for the client secret from backend */
  | "fetching-client-secret"
  /** Waiting for the confirmPayment webhook to finish */
  | "pending-webhook"
  | "error";

/**
 * The value provided by the StripePaymentContext
 */
export interface StripePaymentContextValue {
  state: StripePaymentContextState;
  lastPaymentAt: Date | null;
}

// Keys/query string parameters used to store the state of the Stripe payment process in the URL
const LAST_PAYMENT_AT_PARAM_NAME = "stripePaymentContextLastPaymentAt";
const STATE_PARAM_NAME = "stripePaymentContextState";

const StripePaymentContext = React.createContext<
  StripePaymentContextValue & {
    /** Clears the state back to idle and no recent payment intent pending */
    clearState: () => void;
    /**
     * Sets state to starting a new payment process and returns the URL to redirect to.
     * Saves things like the start time of the payment onto the query string.
     *
     * Does not actually intitiate a payment confirmation. This is done by the caller.
     * */
    setStartPaymentState: () => URL;
    /**
     * Sets the state to a new value.
     */
    setState: (
      value: Exclude<StripePaymentContextState, "pending-webhook">
    ) => void;
  }
>({
  // Just a harmless default value if there's no provider
  state: "idle",
  lastPaymentAt: null,
  clearState: () => {},
  setStartPaymentState: () => new URL(location.href),
  setState: () => {},
});

interface Props extends PropsWithChildren {
  /**
   * The timeout in milliseconds after which the Stripe payment context will be cleared
   * if the payment confirmation is not received. Defaults to 20 seconds.
   */
  paymentConfirmationTimeout?: number;
}

/**
 * A context provider to manage the state of a Stripe payment process
 *
 * We can't store state locally in mem becuase of the async nature of Stripe
 * transactions--they involve redirects and waiting for webhooks and multiple
 * page reloads.
 *
 * So instead we store the state in the URL query string. This way, the state
 * persists across page reloads and redirects.
 */
export function StripePaymentContextProvider({
  children,
  paymentConfirmationTimeout = 60e3,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the last payment time from the query string
  const lastPaymentAt = new Date(
    Number(searchParams.get(LAST_PAYMENT_AT_PARAM_NAME))
  );

  // Get the state from the query string or default to idle
  const state =
    (searchParams.get(STATE_PARAM_NAME) as StripePaymentContextState) ?? "idle";

  // Set the state to a new value
  // Use setStartPaymentState() to start a new payment process, ie. to set state to "pending-webhook"
  const setState = (
    value: Exclude<StripePaymentContextState, "pending-webhook">
  ) => {
    setSearchParams({ [STATE_PARAM_NAME]: value });
  };

  const clearState = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const setStartPaymentState = () => {
    const redirectUrl = new URL(window.location.href);
    redirectUrl.searchParams.set(
      STATE_PARAM_NAME,
      "pending-webhook" satisfies StripePaymentContextState
    );
    redirectUrl.searchParams.set(
      LAST_PAYMENT_AT_PARAM_NAME,
      Date.now().toString()
    );
    setSearchParams({
      [`${STATE_PARAM_NAME}`]:
        "pending-webhook" satisfies StripePaymentContextState,
      [`${LAST_PAYMENT_AT_PARAM_NAME}`]: Date.now().toString(),
    });

    return redirectUrl;
  };

  // Setup a timer to clear the context after the payment confirmation timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      // If timeout has elapsed and we're still waiting for the webhook, clear the state
      // We're giving up and don't want to block UI forever
      if (state === "pending-webhook") clearState();
    }, paymentConfirmationTimeout);
    return () => clearTimeout(timer);
  }, [state, paymentConfirmationTimeout, clearState]);

  return (
    <StripePaymentContext.Provider
      value={{
        state,
        lastPaymentAt,
        clearState,
        setState,
        setStartPaymentState,
      }}
    >
      {children}
    </StripePaymentContext.Provider>
  );
}

export const useStripePaymentContext = () => {
  const contextValue = useContext(StripePaymentContext);
  return contextValue;
};
